import { SyntheticEvent, useEffect, useMemo, useState } from "react";
import { Box, HStack } from "@chakra-ui/react";
import { useRouter } from "next/router";
import { useTranslation } from "next-i18next";
import { EnumActivityType, useGenericActivityMutation } from "src/api/client/generic-activity.api";
import { ButtonLink } from "src/components/base/button";
import { WfHeading } from "src/components/base/wf-heading";
import { IUserReactionWithBarConfig } from "src/components/user-reactions/types";
import { UserReactionBar } from "src/components/user-reactions/user-reaction-bar";
import { UserReactionBarLabel } from "src/components/user-reactions/user-reaction-bar-label";
import { hasUserRevealedReactions, setUserRevealedReactions } from "src/components/user-reactions/utils";
import { useLinkBuilder } from "src/hooks/use-link-builder";
import { useUserStore } from "src/stores/user-store";

export interface IUserReactionsProps {
    reactions: ReadonlyArray<IUserReactionWithBarConfig>;
    highestReactionValue: number;
    onReactionChange: (index: number) => (value: number) => void;
    allReactionsCount: number;
}

const USER_REACTIONS_SHOW_ALL_RESULTS_BUTTON = "user-reactions-show-all-results-button";

export const UserReactions = ({ reactions, highestReactionValue, onReactionChange, allReactionsCount }: IUserReactionsProps) => {
    const { query } = useRouter();
    const { isLoggedIn } = useUserStore();
    const { openLoginModal } = useLinkBuilder();
    const trackActivity = useGenericActivityMutation();
    const { t } = useTranslation("news");
    const hasUserReactedToAll = useMemo(() => reactions.every(reaction => reaction.hasUserReacted), [reactions]);
    const [showAllBars, setShowAllBars] = useState<boolean>(hasUserReactedToAll);

    useEffect(() => {
        if (hasUserRevealedReactions(query.slug as string)) {
            setShowAllBars(true);
        }
    }, [query.slug]);

    useEffect(() => {
        setShowAllBars(prevState => prevState || hasUserReactedToAll);
    }, [hasUserReactedToAll]);

    const handleShowAllResultsClick = (e: SyntheticEvent) => {
        if (!isLoggedIn) {
            e.preventDefault();
            return openLoginModal();
        }

        setShowAllBars(true);
        setUserRevealedReactions(query.slug as string);
        trackActivity({ activityTypeId: EnumActivityType.ReactionsRevealed, parameter: "Reactions revealed" });
    };

    return (
        <Box mt={4} w="100%">
            <WfHeading as="h3" fontSize="xl" mb={2}>
                {t("sub-headline.reactions")}
            </WfHeading>
            <HStack width="100%" justify="space-between" wrap="wrap" spacing={[3, 4, 3, 4]}>
                {reactions.map((reaction, index) => (
                    <Box key={index} maxWidth={["100%", "315px", "100%", "322px"]} width="100%">
                        <UserReactionBar
                            label={
                                <UserReactionBarLabel
                                    label={reaction.label}
                                    underlyingIsin={reaction.underlyingIsin}
                                    underlyingName={reaction.underlyingName}
                                    underlyingLinkTarget="_self"
                                />
                            }
                            reactionValues={reaction.reactionValues}
                            highestReactionValue={highestReactionValue}
                            selectedValue={reaction.hasUserReacted ? String(reaction.userReactionValue) : undefined}
                            onReactionChange={onReactionChange(index)}
                            hideBar={!reaction.hasUserReacted && !showAllBars}
                        />
                    </Box>
                ))}
                {allReactionsCount > 0 ? (
                    <Box textAlign="center" w="100%">
                        <ButtonLink
                            disabled={showAllBars}
                            sx={showAllBars ? { color: "gray.200", _hover: { color: "gray.200" }, cursor: "default" } : undefined}
                            onClick={handleShowAllResultsClick}
                            data-test-id={USER_REACTIONS_SHOW_ALL_RESULTS_BUTTON}
                        >
                            {t("buttons.show-all-results")}
                        </ButtonLink>
                    </Box>
                ) : null}
            </HStack>
        </Box>
    );
};
